import { getFirestore, collection, addDoc, getDocs, updateDoc, deleteDoc, doc, getDoc, query, where, setDoc, DocumentData, CollectionReference, Query, WhereFilterOp } from "firebase/firestore";
import { getFirebaseApp } from "./app";
import { requireAuth, requireOwnership } from "../utils/authSecurity";
import { checkRateLimit, RATE_LIMITS } from "../utils/rateLimiter";

export type WithId<T = DocumentData> = T & { id: string };

export function getDb() {
  return getFirestore(getFirebaseApp());
}

export function getCollectionRef<T = DocumentData>(path: string): CollectionReference<T> {
  return collection(getDb(), path) as CollectionReference<T>;
}

export async function createDocument<T extends DocumentData>(path: string, data: T): Promise<string> {
  checkRateLimit(RATE_LIMITS.FIRESTORE_CREATE);
  const ref = await addDoc(getCollectionRef<T>(path), data);
  return ref.id;
}

export async function createDocumentWithId<T extends DocumentData>(path: string, id: string, data: T): Promise<void> {
  checkRateLimit(RATE_LIMITS.FIRESTORE_CREATE);
  await setDoc(doc(getDb(), path, id), data);
}

export async function readCollection<T extends DocumentData>(path: string, filters?: Array<{ field: string; op: WhereFilterOp; value: unknown }>): Promise<Array<WithId<T>>> {
  checkRateLimit(RATE_LIMITS.FIRESTORE_READ);
  let q: Query<T> = getCollectionRef<T>(path);
  if (filters && filters.length) {
    const constraints = filters.map((f) => where(f.field as string, f.op, f.value));
    q = query(q, ...constraints);
  }
  const snap = await getDocs(q as Query<T>);
  return snap.docs.map((d) => ({ id: d.id, ...(d.data() as T) }));
}

export async function readDocument<T extends DocumentData>(path: string, id: string, requireUserOwnership: boolean = false): Promise<WithId<T> | null> {
  checkRateLimit(RATE_LIMITS.FIRESTORE_READ);
  const d = await getDoc(doc(getDb(), path, id));
  if (!d.exists()) return null;
  const data = d.data() as T;
  
  // If this is a user document, verify ownership
  if (requireUserOwnership && path === "user") {
    requireOwnership((data as any).id || id);
  }
  
  return { id: d.id, ...data };
}

export async function updateDocument<T extends DocumentData>(path: string, id: string, data: Partial<T>, requireUserOwnership: boolean = false): Promise<void> {
  checkRateLimit(RATE_LIMITS.FIRESTORE_UPDATE);
  // If this is a user document, verify ownership before updating
  if (requireUserOwnership && path === "user") {
    const existingDoc = await readDocument<T>(path, id, false);
    if (existingDoc) {
      requireOwnership((existingDoc as any).id || id);
    } else {
      requireAuth(); // At least verify authentication if document doesn't exist
    }
  }
  
  await updateDoc(doc(getDb(), path, id), data as any);
}

export async function deleteDocument(path: string, id: string, requireUserOwnership: boolean = false): Promise<void> {
  checkRateLimit(RATE_LIMITS.FIRESTORE_DELETE);
  // If this is a user document, verify ownership before deleting
  if (requireUserOwnership && path === "user") {
    const existingDoc = await readDocument(path, id, false);
    if (existingDoc) {
      requireOwnership((existingDoc as any).id || id);
    } else {
      requireAuth(); // At least verify authentication if document doesn't exist
    }
  }
  
  await deleteDoc(doc(getDb(), path, id));
}


